import { Injectable } from '@angular/core';
import {
  BullhornEntityTypes,
  BullhornNamespace,
  ISalesforceObjectMapping,
  SalesforceDataSourceApiService,
  SalesforceNamespace,
} from 'datasource-service-lib';
import { InveniasEntityTypes, InveniasNamespace } from 'datasource-service-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { TemplateWorkflowConfig } from 'interfaces-lib';
import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, map, switchMap, take, toArray } from 'rxjs/operators';
import { TemplateService } from './template.service';

@Injectable({ providedIn: 'root' })
export class TemplateListViewService {
  constructor(
    private templateService: TemplateService,
    private salesforceDataSourceApiService: SalesforceDataSourceApiService,
    private featureFlagService: FeatureFlagService,
  ) {}

  getTemplates(namespace: string, entityType: string, entityId: string): Observable<TemplateWorkflowConfig[]> {
    if (namespace === SalesforceNamespace.name) {
      return this.salesforceGetTemplateRequests(entityType, entityId).pipe(
        switchMap((requests) => this.flattenTemplateRequests(requests))
      );
    }

    const obs$ = [this.templateService.getByEntity(entityType, entityType, entityId)];
    if (namespace === InveniasNamespace.name) {
      obs$.push(...this.inveniasGetAdditionalTemplates(entityType));
    } else if (namespace === BullhornNamespace.name) {
      obs$.push(...this.bullhornGetAdditionalTemplates(entityType, entityId));
    }

    return this.flattenTemplateRequests(obs$);
  }

  private flattenTemplateRequests(obs$: Observable<TemplateWorkflowConfig[]>[]): Observable<TemplateWorkflowConfig[]> {
    return forkJoin(obs$).pipe(
      concatMap((r) => r.flat()),
      toArray()
    );
  }

  private inveniasGetAdditionalTemplates(entityType: string): Observable<TemplateWorkflowConfig[]>[] {
    if (entityType === InveniasEntityTypes.Assignments) {
      return [this.templateService.getByEntity(InveniasEntityTypes.People)];
    }
    return [];
  }

  private bullhornGetAdditionalTemplates(entityType: string, entityId: string): Observable<TemplateWorkflowConfig[]>[] {
    switch (entityType) {
      case BullhornEntityTypes.JobOrder:
        return [
          this.templateService.getByEntity(BullhornEntityTypes.Candidate, entityType, entityId),
          this.templateService.getByEntity(BullhornEntityTypes.JobSubmission, entityType, entityId),
        ];
      case BullhornEntityTypes.ClientCorporation:
        return [this.templateService.getByEntity(BullhornEntityTypes.ClientContact, entityType, entityId)];
      default:
        return [];
    }
  }

  private salesforceGetTemplateRequests(entityType: string, entityId: string): Observable<Observable<TemplateWorkflowConfig[]>[]> {
    const baseTemplateRequest = this.templateService.getByEntity(entityType, entityType, entityId);
    if (!this.featureFlagService.enabled(FeatureFlagService.FLAGS.salesforceRelatedTemplates)()) {
      return of([baseTemplateRequest]);
    }

    return this.salesforceDataSourceApiService.getEntityMappings().pipe(
      take(1),
      map((mappings) => {
        const relatedTemplateEntityTypes = this.salesforceRelatedTemplateEntityTypes(mappings, entityType);
        return [
          baseTemplateRequest,
          ...relatedTemplateEntityTypes.map((templateEntityType) =>
            this.templateService.getByEntity(templateEntityType, entityType, entityId)
          ),
        ];
      })
    );
  }

  private salesforceRelatedTemplateEntityTypes(mappings: ISalesforceObjectMapping[], entityType: string): string[] {
    return [...new Set(
      (mappings ?? [])
        .filter((mapping) => mapping.objectName?.toLowerCase() !== entityType.toLowerCase())
        .filter((mapping) =>
          (mapping.relatedObjects ?? []).some((related) => related.objectName.toLowerCase() === entityType.toLowerCase())
        )
        .map((mapping) => mapping.objectName)
    )];
  }
}
